import { isBrowser } from "@/utils/runtime";

type AnalyticsRange = "24h" | "7d" | "30d" | "90d";

export interface AnalyticsSummary {
  totalVisits: number;
  uniqueVisitors: number;
  pageViews: number;
  bounceRate?: number | null;
  visitDuration?: number | null;
  start: string;
  end: string;
}

export interface RegionSummary {
  code: string;
  name: string;
  visits: number;
  percentage: number;
}

export interface PageSummary {
  path: string;
  visits: number;
  pageViews: number;
}

interface UsageResponse {
  [key: string]: any;
}

const API_BASE_URL = "https://api.vercel.com";

function getEnv(name: string): string | undefined {
  const value = import.meta.env[name as keyof ImportMetaEnv];
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function resolveRange(range: AnalyticsRange) {
  const now = new Date();
  const end = now.toISOString();
  const startDate = new Date(now);

  switch (range) {
    case "24h":
      startDate.setDate(now.getDate() - 1);
      break;
    case "7d":
      startDate.setDate(now.getDate() - 7);
      break;
    case "30d":
      startDate.setDate(now.getDate() - 30);
      break;
    case "90d":
      startDate.setDate(now.getDate() - 90);
      break;
    default:
      startDate.setDate(now.getDate() - 7);
  }

  return {
    start: startDate.toISOString(),
    end,
  };
}

function normalizeSummary(data: UsageResponse, range: { start: string; end: string }): AnalyticsSummary {
  const totals = data?.totals ?? data ?? {};

  const totalVisits =
    totals?.totalVisits ??
    totals?.visits ??
    totals?.total ??
    totals?.analytics?.visits ??
    0;

  const uniqueVisitors =
    totals?.uniqueVisitors ??
    totals?.visitors ??
    totals?.unique ??
    totals?.analytics?.visitors ??
    totalVisits;

  const pageViews =
    totals?.pageViews ??
    totals?.views ??
    totals?.pageviews ??
    totals?.analytics?.pageViews ??
    totalVisits;

  const bounceRate =
    totals?.bounceRate ??
    totals?.bounce_rate ??
    totals?.analytics?.bounceRate ??
    null;

  const visitDuration =
    totals?.visitDuration ??
    totals?.visit_duration ??
    totals?.avgDuration ??
    totals?.analytics?.visitDuration ??
    null;

  return {
    totalVisits,
    uniqueVisitors,
    pageViews,
    bounceRate,
    visitDuration,
    start: range.start,
    end: range.end,
  };
}

function normalizeRegions(data: UsageResponse, summary?: AnalyticsSummary): RegionSummary[] {
  const regions =
    data?.regions ??
    data?.topRegions ??
    data?.topCountries ??
    data?.countries ??
    [];

  const totalVisits = summary?.totalVisits ??
    (Array.isArray(regions)
      ? regions.reduce((acc: number, item: any) => acc + (item?.visits ?? item?.value ?? 0), 0)
      : 0);

  if (!Array.isArray(regions)) {
    return [];
  }

  return regions
    .map((region: any) => {
      const visits = region?.visits ?? region?.value ?? region?.count ?? 0;
      const code = (region?.code ?? region?.country ?? region?.location ?? "").toString();
      const name = region?.name ?? region?.region ?? region?.label ?? code;
      const percentage = totalVisits > 0 ? (visits / totalVisits) * 100 : 0;

      return {
        code,
        name,
        visits,
        percentage,
      };
    })
    .filter((item: RegionSummary) => item.visits > 0)
    .sort((a: RegionSummary, b: RegionSummary) => b.visits - a.visits);
}

function normalizePages(data: UsageResponse): PageSummary[] {
  const pages =
    data?.pages ??
    data?.topPages ??
    data?.topPaths ??
    data?.urls ??
    [];

  if (!Array.isArray(pages)) {
    return [];
  }

  return pages
    .map((page: any) => {
      const visits = page?.visits ?? page?.value ?? page?.count ?? page?.pageVisits ?? 0;
      const pageViews = page?.pageViews ?? page?.views ?? page?.pageviews ?? visits;
      const path = page?.path ?? page?.url ?? page?.route ?? "/";

      return {
        path,
        visits,
        pageViews,
      };
    })
    .filter((item: PageSummary) => item.pageViews > 0)
    .sort((a: PageSummary, b: PageSummary) => b.pageViews - a.pageViews);
}

async function fetchFromVercel(path: string, params: URLSearchParams, signal?: AbortSignal) {
  const token = getEnv("VITE_VERCEL_API_TOKEN");

  if (!token) {
    throw new Error("Token da API da Vercel não configurado (VITE_VERCEL_API_TOKEN)");
  }

  const url = `${API_BASE_URL}${path}?${params.toString()}`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
    signal,
  });

  if (!response.ok) {
    const message = await response.text();
    throw new Error(`Erro ao consultar Vercel (${response.status}): ${message}`);
  }

  return response.json();
}

function buildSearchParams(range: { start: string; end: string }): URLSearchParams {
  const params = new URLSearchParams();
  params.set("from", range.start);
  params.set("to", range.end);
  params.set("unit", "1d");

  const teamId = getEnv("VITE_VERCEL_TEAM_ID");
  const projectId = getEnv("VITE_VERCEL_PROJECT_ID");
  const siteId = getEnv("VITE_VERCEL_ANALYTICS_SITE_ID");
  const domain = getEnv("VITE_VERCEL_ANALYTICS_DOMAIN");

  if (teamId) {
    params.set("teamId", teamId);
  }

  if (projectId) {
    params.set("projectId", projectId);
    params.set("appId", projectId);
  }

  if (siteId) {
    params.set("siteId", siteId);
  }

  if (domain) {
    params.set("domain", domain);
  }

  return params;
}

export const vercelAnalyticsService = {
  async getOverview(range: AnalyticsRange = "7d", signal?: AbortSignal) {
    if (!isBrowser) {
      return {
        summary: undefined,
        regions: [] as RegionSummary[],
        pages: [] as PageSummary[],
      };
    }

    const resolvedRange = resolveRange(range);
    const params = buildSearchParams(resolvedRange);

    try {
      const usageData = await fetchFromVercel("/v1/analytics/usage", params, signal);
      const summary = normalizeSummary(usageData, resolvedRange);
      const regions = normalizeRegions(usageData, summary);
      const pages = normalizePages(usageData);

      if (regions.length === 0) {
        try {
          const regionsData = await fetchFromVercel("/v1/analytics/regions", params, signal);
          const fallbackRegions = normalizeRegions(regionsData, summary);
          if (fallbackRegions.length > 0) {
            return { summary, regions: fallbackRegions, pages };
          }
        } catch (error) {
          console.warn("Não foi possível carregar regiões detalhadas da Vercel", error);
        }
      }

      return { summary, regions, pages };
    } catch (error) {
      console.error("Erro ao buscar analytics da Vercel:", error);
      throw error;
    }
  },
};

export type { AnalyticsRange };
